/**
 * OpossumNest — hidey-hole where baby opossums seek safety (Babylon.js port).
 *
 * When an opossum enters the nest's overlap zone, it hides inside.
 * The stealth escort puzzle is complete when all nests are occupied.
 *
 * Visual: a rounded nest shape with a warm glow when occupied.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { InteractiveObject, InteractiveConfig } from "./InteractiveObject";
import { COLORS } from "../../config/GameConfig";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { ConservatoryBurrowVisuals, type ConservatoryBurrowOptions } from "../../levels/art/ConservatoryBurrowVisuals";

export interface OpossumNestConfig extends Omit<InteractiveConfig, "size"> {
  size?: Vector3;
  visuals?: ConservatoryBurrowOptions;
}

export class OpossumNest extends InteractiveObject {
  private _isOccupied = false;

  /** Invisible overlap volume for opossum entry detection. */
  public overlapMesh: Mesh;

  /** Visible nest body. */
  private nestBody: Mesh | null = null;
  private burrow: ConservatoryBurrowVisuals | null = null;

  /** Warm glow point light, only on when occupied. */
  private nestGlow: PointLight | null = null;
  private rootMat?: StandardMaterial;
  private mossMat?: StandardMaterial;
  private lanternMat?: StandardMaterial;

  constructor(scene: Scene, config: OpossumNestConfig) {
    const size = config.size ?? new Vector3(2.4, 1.2, 1.5);
    super(scene, { ...config, size });

    // Quest overlap remains at the original logical position and size.
    this.overlapMesh = MeshBuilder.CreateBox(`nest_${config.key}_overlap`,
      { width: size.x * 1.3, height: size.y, depth: size.z * 1.3 }, scene);
    this.overlapMesh.parent = this.rootMesh;
    this.overlapMesh.isVisible = false; this.overlapMesh.isPickable = false;
    if (config.visuals) {
      this.burrow = new ConservatoryBurrowVisuals(scene, this.rootMesh, config.visuals);
      return;
    }

    // --- Visible nest body (half-sphere / bowl shape approximation) ---
    this.nestBody = MeshBuilder.CreateSphere(
      `nest_${config.key}_body`,
      { diameter: size.x, slice: 0.5, segments: 12 },
      scene,
    );
    this.nestBody.parent = this.rootMesh;
    this.nestBody.position = new Vector3(0, -size.y * 0.25, 0);
    this.nestBody.rotation.x = Math.PI; // flip hemisphere to form a bowl

    const nestMat = new StandardMaterial(`nest_${config.key}_mat`, scene);
    nestMat.diffuseColor = new Color3(0.3, 0.2, 0.12); // dark woven material
    nestMat.specularColor = Color3.Black();
    nestMat.emissiveColor = new Color3(0.02, 0.01, 0.005);
    this.nestBody.material = nestMat;
    this.material = nestMat;

    this.rootMat = new StandardMaterial(`nest_${config.key}_rootMat`, scene);
    this.rootMat.diffuseColor = Color3.FromHexString("#4b2b16");
    this.rootMat.emissiveColor = new Color3(0.02, 0.01, 0.005);
    this.rootMat.specularColor = Color3.Black();

    this.mossMat = new StandardMaterial(`nest_${config.key}_mossMat`, scene);
    this.mossMat.diffuseColor = Color3.FromHexString("#647b45");
    this.mossMat.emissiveColor = new Color3(0.03, 0.04, 0.02);
    this.mossMat.specularColor = Color3.Black();

    this.lanternMat = new StandardMaterial(`nest_${config.key}_lanternMat`, scene);
    this.lanternMat.diffuseColor = Color3.FromHexString("#b0d5a9");
    this.lanternMat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN).scale(0.25);
    this.lanternMat.specularColor = Color3.Black();
    this.lanternMat.alpha = 0.78;

    const rootArch = MeshBuilder.CreateTorus(
      `nest_${config.key}_rootArch`,
      { diameter: size.x * 0.9, thickness: 0.18, tessellation: 20 },
      scene,
    );
    rootArch.parent = this.rootMesh;
    rootArch.position = new Vector3(0, 0.2, -0.15);
    rootArch.rotation.x = Math.PI / 2;
    rootArch.material = this.rootMat;

    const rootShell = MeshBuilder.CreateSphere(
      `nest_${config.key}_rootShell`,
      { diameterX: size.x * 0.92, diameterY: size.y * 1.05, diameterZ: size.z * 0.65, segments: 12 },
      scene,
    );
    rootShell.parent = this.rootMesh;
    rootShell.position = new Vector3(0, 0.12, -0.48);
    rootShell.material = this.rootMat;

    const mossPad = MeshBuilder.CreateSphere(
      `nest_${config.key}_mossPad`,
      { diameterX: size.x * 0.72, diameterY: size.y * 0.42, diameterZ: size.z * 0.52, segments: 10 },
      scene,
    );
    mossPad.parent = this.rootMesh;
    mossPad.position = new Vector3(0, -0.08, 0.02);
    mossPad.material = this.mossMat;

    for (const [index, side] of [-1, 1].entries()) {
      const glowFruit = MeshBuilder.CreateSphere(
        `nest_${config.key}_glowFruit_${index}`,
        { diameter: 0.18, segments: 8 },
        scene,
      );
      glowFruit.parent = this.rootMesh;
      glowFruit.position = new Vector3(side * 0.46, 0.28, -0.06);
      glowFruit.material = this.lanternMat;
    }

  }

  // ─── Public API ───────────────────────────────────────────────

  get isOccupied(): boolean {
    return this._isOccupied;
  }

  /**
   * Accept an opossum into this nest.
   * Triggers glow effect and emits event.
   */
  acceptOpossum(): void {
    if (this._isOccupied) return;

    this._isOccupied = true;
    this.state = "active";
    this.updateMaterialForState();

    if (this.burrow) {
      this.burrow.setOccupied(true);
      gameEvents.emit(GameEvents.NEST_CAPTURED, { nestKey: this.key });
      return;
    }

    // Contentment glow
    if (!this.nestGlow) {
      this.nestGlow = new PointLight(
        `nest_${this.key}_glowLight`,
        this.rootMesh.position.add(new Vector3(0, 0.5, 0)),
        this.scene,
      );
      this.nestGlow.diffuse = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN);
      this.nestGlow.intensity = 0.8;
      this.nestGlow.range = 4;
    }

    // Tint the nest body green
    const mat = this.nestBody!.material as StandardMaterial;
    mat.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN).scale(0.15);
    this.mossMat!.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN).scale(0.18);
    this.lanternMat!.emissiveColor = Color3.FromHexString(COLORS.BIOLUMINESCENT_GREEN).scale(0.5);

    // Forensic-QA event: nest occupancy is a top-line escort milestone.
    // OPOSSUM_HIDDEN already fires from Opossum.hide(), but that doesn't
    // identify which nest actually received the opossum. NEST_CAPTURED
    // gives telemetry a 1:1 mapping between nest key and capture event.
    gameEvents.emit(GameEvents.NEST_CAPTURED, { nestKey: this.key });
  }

  dispose(): void {
    this.burrow?.dispose();
    this.overlapMesh.dispose();
    this.nestBody?.dispose();
    this.rootMat?.dispose();
    this.mossMat?.dispose();
    this.lanternMat?.dispose();
    if (this.nestGlow) {
      this.nestGlow.dispose();
    }
    super.dispose();
  }
}
